import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Motion";
import { scrollToId } from "./SmoothScroll";
import { DOMAINES } from "@/lib/content";

const Card = ({ item, index, featured, className }) => (
  <motion.article
    data-testid={`domaine-card-${item.k}`}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.9, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -6 }}
    className={`group relative flex flex-col justify-between overflow-hidden p-8 sm:p-10 ${className} ${
      featured ? "bg-[#3D5A4C] text-[#FAF8F5]" : "bg-white text-[#1C2826] border border-[#3D5A4C]/12"
    }`}
  >
    {featured && (
      <svg viewBox="0 0 200 240" className="pointer-events-none absolute -right-10 -top-6 h-[120%] opacity-[0.12]" aria-hidden="true">
        <path d="M10 240V100a90 90 0 0 1 180 0v140" fill="none" stroke="#E5DEC9" strokeWidth="1" />
        <path d="M40 240V105a60 60 0 0 1 120 0v135" fill="none" stroke="#E5DEC9" strokeWidth="1" />
      </svg>
    )}
    <div className="relative flex items-start justify-between">
      <span className={`font-serif-d text-lg italic ${featured ? "text-[#D9C193]" : "text-[#B39158]"}`}>0{index + 1}</span>
      <ArrowUpRight size={20} strokeWidth={1.2} className="opacity-50 transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:opacity-100" />
    </div>
    <div className="relative mt-16">
      <h3 className={`font-serif-d font-normal leading-[1.05] ${featured ? "text-4xl sm:text-5xl" : "text-[1.9rem]"}`}>{item.t}</h3>
      <span className={`mt-5 block h-px w-10 transition-[width] duration-700 group-hover:w-24 ${featured ? "bg-[#D9C193]" : "bg-[#B39158]"}`} />
      <p className={`mt-5 max-w-xl text-[0.98rem] leading-relaxed ${featured ? "text-[#E8EEEA]" : "text-[#3E4A47]"}`}>{item.d}</p>
      {item.tags && (
        <div className="mt-6 flex flex-wrap gap-2">
          {item.tags.map((t) => <span key={t} className="rounded-full border border-[#E5DEC9]/40 px-3 py-1 text-xs text-[#E5DEC9]">{t}</span>)}
        </div>
      )}
    </div>
  </motion.article>
);

export const Domaines = () => (
  <section id="domaines" data-testid="domaines-section" className="bg-[#F3EFE8] py-28 sm:py-36">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal><SectionLabel index="02">Domaines d'intervention</SectionLabel></Reveal>
          <h2 className="font-serif-d mt-8 text-4xl font-light leading-[1.08] text-[#1C2826] sm:text-5xl lg:text-6xl">
            <MaskLines lines={["Toutes les victimes,", <>tous les <em className="italic text-[#3D5A4C]">préjudices</em>.</>]} />
          </h2>
        </div>
        <Reveal className="lg:col-span-4 lg:col-start-9">
          <p className="text-base leading-relaxed text-[#3E4A47]">
            Que le dommage résulte d'un accident, d'un acte médical ou d'une infraction, le cabinet RC AVOCATS intervient à vos côtés,
            à l'amiable comme devant les juridictions et commissions compétentes.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid auto-rows-[minmax(340px,auto)] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6 lg:gap-5">
        <Card item={DOMAINES[0]} index={0} featured className="md:col-span-2 lg:col-span-4" />
        <Card item={DOMAINES[1]} index={1} className="lg:col-span-2" />
        <Card item={DOMAINES[2]} index={2} className="lg:col-span-2" />
        <Card item={DOMAINES[3]} index={3} className="lg:col-span-2" />
        <Card item={DOMAINES[4]} index={4} className="md:col-span-2 lg:col-span-2" />
      </div>

      <Reveal className="mt-12 flex flex-col items-start justify-between gap-6 border-t border-[#3D5A4C]/15 pt-8 sm:flex-row sm:items-center">
        <p className="font-serif-d text-2xl italic text-[#1C2826]">Vous ne savez pas si votre situation ouvre droit à indemnisation ?</p>
        <button data-testid="domaines-contact-button" onClick={() => scrollToId("contact")}
          className="link-underline pb-1 text-[0.95rem] font-medium text-[#3D5A4C]">Exposez-la au cabinet →</button>
      </Reveal>
    </div>
  </section>
);
